import React, { useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { userDataSelector } from '@selectors/global';
import { isMobile } from '@utils';

const mapStateToProps = createStructuredSelector({
  userData: userDataSelector,
});

let timer = null;

const AgGrid = ({
  fetchPreference,
  userData,
  disablePreferenceSave,
  autoResize,
  autoResizeToFit,
  columnDefs,
  enableSortingSave,
  name,
  persistPreference,
  preference,
  allowHorizontalScroll,
  ...otherProps
}) => {
  const api = useRef();
  const columnApi = useRef();

  useEffect(() => {
    if (disablePreferenceSave || typeof fetchPreference !== 'function') {
      return;
    }

    fetchPreference({
      userId: userData.get('id'),
    });
  }, []);

  const autoSizeAll = (params) => {
    if (autoResizeToFit) {
      params.api.sizeColumnsToFit();
      return;
    }

    if (autoResize) {
      if (!params.columnApi) {
        return;
      }
      params.columnApi.autoSizeAllColumns();
    }
  };

  const saveSortModel = (sortModel, name) => {
    if (window.localStorage) {
      const sortModels = window.localStorage.getItem('sortModels');
      let sortObject = {};

      if (sortModels) {
        sortObject = JSON.parse(sortModels);
      }

      sortObject[name] = sortModel;
      window.localStorage.setItem('sortModels', JSON.stringify(sortObject));
    }
  };

  const applySortModel = () => {
    if (window.localStorage && enableSortingSave) {
      const sortModels = window.localStorage.getItem('sortModels');
      let sortObject = {};

      if (sortModels) {
        sortObject = JSON.parse(sortModels);
      }

      if (sortObject[name] && sortObject[name].length > 0) {
        return columnDefs.map((def) => {
          if (def.field === sortObject[name][0].colId) {
            return {
              ...def,
              sort: sortObject[name][0].sort,
            };
          }

          return def;
        });
      }
    }
    return columnDefs;
  };

  const onSavePreference = (params) => {
    if (disablePreferenceSave || typeof persistPreference !== 'function') {
      return;
    }

    clearTimeout(timer);

    timer = setTimeout(() => {
      const columnsWidth = {};
      const columnsPinnedState = {};

      params.columnApi.getColumnState().forEach((column) => {
        columnsWidth[column.colId] = column.width;
        columnsPinnedState[column.colId] = column.pinned;
      });

      const movedColumns = params.columnApi
        .getColumnState()
        .map((column) => column.colId);
      const visibleColumns = params.columnApi
        .getAllDisplayedColumns()
        .map((column) => column.colId);
      const preferenceToPersist = preference.get('data').toJS();
      preferenceToPersist[name] = {
        ...preferenceToPersist[name],
        columnsWidth,
        movedColumns,
        visibleColumns,
        columnsPinnedState,
      };
      if ('select' in preferenceToPersist[name].columnsWidth) {
        delete preferenceToPersist[name].columnsWidth['select'];
      }
      if ('select' in preferenceToPersist[name].columnsPinnedState) {
        delete preferenceToPersist[name].columnsPinnedState['select'];
      }
      for (let x = 0; x < preferenceToPersist[name].movedColumns.length; x++) {
        if (preferenceToPersist[name].movedColumns[x] === 'select') {
          preferenceToPersist[name].movedColumns.splice(x, 1);
        }
      }
      for (
        let x = 0;
        x < preferenceToPersist[name].visibleColumns.length;
        x++
      ) {
        if (preferenceToPersist[name].visibleColumns[x] === 'select') {
          preferenceToPersist[name].visibleColumns.splice(x, 1);
        }
      }

      persistPreference({
        userId: userData.get('id'),
        data: {
          preference: preferenceToPersist,
        },
      });
    }, 500);
  };

  const onGridReadyFunc = (params) => {
    api.current = params.api;
    columnApi.current = params.api;

    if (otherProps.onGridReady) {
      otherProps.onGridReady(params);
    }

    if (autoResize || autoResizeToFit) {
      setTimeout(() => autoSizeAll(params), 10);
    }
  };

  const onRowDataUpdatedFunc = (params) => {
    if (autoResize || autoResizeToFit) {
      setTimeout(() => autoSizeAll(params), 10);
    }

    if (otherProps.onRowDataUpdated) {
      otherProps.onRowDataUpdated(params);
    }
  };

  const onColumnPinnedFunc = (params) => {
    if (otherProps.onColumnPinned) {
      otherProps.onColumnPinned(params);
    }

    onSavePreference(params);
  };

  const onColumnResizedFunc = (params) => {
    if (otherProps.onColumnResized) {
      otherProps.onColumnResized(params);
    }

    onSavePreference(params);
  };

  const onColumnVisibleFunc = (params) => {
    if (otherProps.onColumnVisible) {
      otherProps.onColumnVisible(params);
    }

    onSavePreference(params);
  };

  const onDragStoppedFunc = (params) => {
    if (otherProps.onDragStopped) {
      otherProps.onDragStopped(params);
    }

    onSavePreference(params);
  };

  const onSortChangedFunc = (params) => {
    if (otherProps.onSortChanged) {
      otherProps.onSortChanged(params);
    }

    if (enableSortingSave) {
      const columns = params.columnApi.getColumnState();
      const changedCol = columns.find((c) => c.sort);
      const sortModel = changedCol ? [changedCol] : [];
      saveSortModel(sortModel, name);
    }
  };

  const allowHorizontalScrollGridStyle = {
    height: '100%',
    minWidth: '100%',
    overflow: 'auto',
  };

  const renderAgGridReact = () => {
    return (
      <AgGridReact
        {...otherProps}
        columnDefs={applySortModel(columnDefs)}
        rowHeight={38}
        suppressMenuHide={isMobile()}
        onColumnPinned={(value) => onColumnPinnedFunc(value)}
        onColumnResized={(value) => onColumnResizedFunc(value)}
        onColumnVisible={(value) => onColumnVisibleFunc(value)}
        onDragStopped={(value) => onDragStoppedFunc(value)}
        onGridReady={(value) => onGridReadyFunc(value)}
        onRowDataUpdated={(value) => onRowDataUpdatedFunc(value)}
        onSortChanged={(value) => onSortChangedFunc(value)}
        suppressColumnVirtualisation={allowHorizontalScroll}
      />
    );
  };

  // if allowHorizontalScroll={true}, then div wrapper is needed. Otherwise, it will break the grid.
  if (allowHorizontalScroll) {
    return (
      <div style={allowHorizontalScrollGridStyle}>{renderAgGridReact()}</div>
    );
  }

  return renderAgGridReact();
};

AgGrid.propTypes = {
  autoResize: PropTypes.bool,
  autoResizeToFit: PropTypes.bool,
  columnDefs: PropTypes.any,
  disablePreferenceSave: PropTypes.bool,
  enableSortingSave: PropTypes.bool,
  fetchPreference: PropTypes.func,
  name: PropTypes.string,
  persistPreference: PropTypes.func,
  preference: ImmutablePropTypes.map,
  userData: ImmutablePropTypes.map,
  allowHorizontalScroll: PropTypes.bool,
};

AgGrid.defaultProps = {
  allowHorizontalScroll: false,
};

export default connect(mapStateToProps, null)(AgGrid);
